"use client";

import { useState } from "react";
import type { ReactNode } from "react";
import { InteractiveTerminal } from "@/components/heroes/interactive-terminal";
import { BootSequence } from "@/components/heroes/boot-sequence";
import type { Flushed } from "@/components/heroes/boot-sequence";
import { RecordLastLogin } from "@/components/record-last-login";
import type { FakeFs } from "@/lib/fake-fs";
import type { BootLine } from "@/lib/hero-current";

/**
 * Render a captured boot transcript (the raw `Flushed[]` lines
 * from BootSequence) as React nodes. Mirrors the rendering
 * inside BootSequence so the visual is identical when the
 * transcript is replayed inside the InteractiveTerminal. The
 * `mode` flag controls whether the line ends with a newline:
 * `inline` entries sit on the same visual line as whatever
 * follows (used for login prompts). Each entry holds an array
 * of segments so a single visual line can carry multiple
 * colors (e.g. the accent-colored "login: " prompt followed by
 * the fg-colored typed username).
 */
function renderBootTranscript(lines: Flushed[]): ReactNode[] {
  return lines.map((line, i) => {
    const trail = line.mode === "inline" ? "" : "\n";
    const isEmpty = line.segs.every((s) => s.text === "");
    if (isEmpty && trail === "") return null;
    if (isEmpty) {
      return <span key={`bt-${i}`}>{"\n"}</span>;
    }
    return (
      <span key={`bt-${i}`}>
        {line.segs.map((seg, j) => (
          <span key={`bt-${i}-${j}`} className={seg.cls}>
            {seg.text}
          </span>
        ))}
        {trail}
      </span>
    );
  });
}

/**
 * Client component. Stages the boot animation, then mounts the
 * real interactive terminal so it picks up focus + history state.
 * The async server wrapper in `hero-current.tsx` does the data
 * fetching (fake fs, last-login timestamp) and passes the results
 * in as props so this client module never has to import
 * server-only code.
 */
export function BootHeroClient({
  bootLines,
  fs,
  lastLoginText,
}: {
  bootLines: BootLine[];
  fs: FakeFs;
  lastLoginText: string;
}) {
  // Two-stage mount: while `booted` is false we render only the
  // client-side boot animation. Once it finishes we mount the
  // real InteractiveTerminal so it picks up focus + history state.
  const [booted, setBooted] = useState(false);
  // Captured boot transcript. The BootSequence hands us the
  // final list of flushed lines when the animation finishes;
  // we then render them as children of the InteractiveTerminal
  // so the boot lines stay visible above the live prompt
  // instead of disappearing when BootStage unmounts.
  const [capturedLines, setCapturedLines] = useState<Flushed[] | null>(null);

  return (
    <section className="flex flex-col flex-1 min-h-0 p-6 max-w-3xl mx-auto w-full cursor-text">
      {booted ? (
        // No prefill — the terminal starts at a fresh prompt.
        // `autoCommands` replays the preset commands (`whoami`,
        // `ls`) by typing them into the input one char at a time
        // and submitting through the normal path, so they render
        // with the correct prompt colors and no extra newline.
        // The captured boot transcript is rendered above the
        // live history so the page reads as one continuous
        // terminal session.
        <InteractiveTerminal fs={fs} autoCommands={["whoami", "ls"]}>
          {capturedLines ? renderBootTranscript(capturedLines) : null}
        </InteractiveTerminal>
      ) : (
        <BootStage
          bootLines={bootLines}
          lastLoginText={lastLoginText}
          onDone={() => setBooted(true)}
          onFinalLines={(lines) => setCapturedLines(lines)}
        />
      )}

      <RecordLastLogin />

      <style>{`
        @keyframes blink { 50% { opacity: 0; } }
        .cursor-blink { animation: blink 1s steps(1) infinite; }
      `}</style>
    </section>
  );
}

/**
 * Boot-stage chrome: same outer shape as InteractiveTerminal
 * (scrollable, focusable, monospace) so when the live terminal
 * mounts in the same slot, nothing jumps. The actual animation
 * is delegated to `BootSequence`.
 */
function BootStage({
  bootLines,
  lastLoginText,
  onDone,
  onFinalLines,
}: {
  bootLines: BootLine[];
  lastLoginText: string;
  onDone: () => void;
  onFinalLines: (lines: Flushed[]) => void;
}) {
  return (
    <div className="flex flex-col flex-1 min-h-0 font-mono text-[0.7rem] sm:text-xs leading-tight">
      <div className="flex-1 min-h-0 overflow-y-auto cursor-text">
        <pre className="whitespace-pre-wrap break-words">
          <BootSequence
            bootLines={bootLines}
            lastLoginText={lastLoginText}
            onDone={onDone}
            onFinalLines={onFinalLines}
          />
        </pre>
      </div>
    </div>
  );
}